import React, { useState, useEffect, useRef } from 'react'
import CustomDataGrid from '../components/CustomDataGrid'
import EntityDetails from '../components/EntityDetails'
import { Button, Alert, Spinner, Modal, Form } from 'react-bootstrap'
import JSZip from 'jszip'
import QRCode from 'qrcode'
import { saveAs } from 'file-saver'
import {
  FaTrashAlt,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaDownload,
  FaInfoCircle
} from 'react-icons/fa'
import {
  campaignGridColumns,
  campaignMapping,
  campaignRowMapping,
  generateFormData,
  generateGridColumns,
  generateGridRows,
  formatUtcToLocal
} from '../utils/PageUtils'
import { getCampaigns, uploadContactsToAPI } from '../utils/api'
import '../styles/Contacts.css'
import PageHelmet from '../components/PageHelmet'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { IconButton } from "@mui/material";
import { MdArrowBackIos } from "react-icons/md";

const Contacts = ({ userRole, bearerToken }) => {
  const fileInputRef = useRef(null)

  const [campaignsData, setCampaignsData] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState(null)

  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState(null)
  const [uploadError, setUploadError] = useState('')
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)
  const [zipFile, setZipFile] = useState(null)
  const [isUploaded, setIsUploaded] = useState(false)
  const [refreshGrid, setRefreshGrid] = useState(true)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const [openUploadContactsModal, setOpenUploadContactsModal] = useState(false)
  const [uploadCampaign, setUploadCampaign] = useState(null);

  const [searchParams] = useSearchParams()
  const testMode = searchParams.get('test-mode')
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  console.log('Test Mode:', testMode)

  useEffect(() => {
    const loadCampaigns = async () => {
      if (!bearerToken) {
        console.warn('No access token available')
        return
      }
      if (campaignsData.length === 0) {
        setIsLoading(true)
      }
      try {
        const data = await getCampaigns(bearerToken)
        if (data?.records) {
          setCampaignsData(data.records)
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error)
      } finally {
        setIsLoading(false)
        if (refreshGrid) {
          setRefreshGrid(false)
        }
      }
    }
    if (refreshGrid) {
      loadCampaigns()
    }
  }, [refreshGrid])

  const handleRowClick = (e) => {
    resetUploadState()
    if (selectedRowId === e.id) {
      setSelectedCampaign(null)
      setSelectedRowId(null)
    } else {
      const selected = campaignsData[e.id - 1]
      setSelectedCampaign(selected)
      setSelectedRowId(e.id)
    }
  }

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0]
    resetMessages()

    if (!uploadedFile) return
    if (uploadedFile.type !== 'text/csv') {
      setUploadError('Only CSV files are allowed.')
      return
    }
    if (uploadedFile.size > 2 * 1024 * 1024) {
      setUploadError('File size should not exceed 2 MB.')
      return
    }
    setFile(uploadedFile)
  }

  const resetMessages = () => {
    setUploadError('')
    setInfo(null)
    if (!isUploaded) {
      setZipFile(null)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = null
    }
  }

  const resetUploadState = () => {
    setFile(null)
    setZipFile(null)
    setInfo(null)
    setError(null)
    setIsUploaded(false)

    if (fileInputRef.current) {
      fileInputRef.current.value = null
    }
  }

  const handleUpload = async () => {
    setUploadError(null)
    setError(null)
    setInfo(null)
    if (!uploadCampaign) {
      setUploadError('Please select a campaign before uploading contacts.')
      return
    }
    if (!file) {
      setUploadError('Please select a file before uploading.')
      return
    }
    setIsUploaded(true)
    const payload = {
      campaign: uploadCampaign.campaign_name,
      destination_url: uploadCampaign.destination_url,
    }

    const result = await uploadContactsToAPI(file, payload, bearerToken)
    if (result.success) {
      const data = result.data
      setInfo(result.message)
      generateQRCodesAndZip(data)
      setFile(null)
      setIsUploaded(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = null
      }
      setUploadCampaign(null)
      setRefreshGrid(true)

    } else {
      setError(result.message)
    }
  }

  useEffect(() => {
    if (!selectedCampaign || campaignsData.length === 0) return

    const updatedCampaign = campaignsData.find(
      (c) => c.campaign_name === selectedCampaign.campaign_name
    )

    if (updatedCampaign) {
      setSelectedCampaign(updatedCampaign)
    }
  }, [campaignsData])


  const generateQRCodesAndZip = async (data) => {
    const zip = new JSZip()
    const qrZip = new JSZip()

    for (const row of data) {
      const url = row['o.rp.trackableurl']
      const name = row['o.rp.qrcode_img']

      if (url !== 'Error' && name) {
        try {
          const qrDataUrl = await QRCode.toDataURL(url, {
            scale: 10,
            margin: 1,
          })
          const qrBlob = await (await fetch(qrDataUrl)).blob()
          qrZip.file(`${name}.png`, qrBlob)
        } catch (err) {
          console.error('QR code generation error:', err)
        }
      }
    }
    const csvString = generateCsvString(data)
    zip.file(
      'processed_contacts.csv',
      new Blob([csvString], { type: 'text/csv' }),
    )
    zip.file('qrcodes.zip', await qrZip.generateAsync({ type: 'blob' }))

    const finalZip = await zip.generateAsync({ type: 'blob' })
    const timestamp = new Date().toISOString().replace(/[-:.]/g, '_')
    const name = selectedCampaign?.campaign_name || 'unknown'

    setZipFile({
      blob: finalZip,
      name: `campaign_${name}_upload_${timestamp}.zip`,
    })
  }

  const generateCsvString = (data) => {
    const headers = Object.keys(data[0]).join(',')
    const body = data.map((row) => Object.values(row).join(',')).join('\n')
    return `${headers}\n${body}`
  }

  const campaignFormData = selectedCampaign
    ? generateFormData(selectedCampaign, campaignMapping)
    : null
  const campaignColumns = generateGridColumns(
    campaignGridColumns,
    'campaign_name',
  )
  const campaignRows = generateGridRows(campaignsData, campaignRowMapping)

  const handleDownloadAndReload = () => {
    saveAs(zipFile.blob, zipFile.name)
    setZipFile(null)
    setIsUploaded(false)
    setFile(null)
    setSelectedCampaign(null)
    setInfo(null)
    setError(null)
    setUploadError('')
    setSelectedRowId(null)
  }

  const handleClearAndRetry = () => {
    setError(null)
    setUploadError('')
    setInfo(null)
    setIsUploaded(false)
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = null
    }
    setZipFile(null)
  }
  const handleCloseCampaignDetails = () => {
    setSelectedCampaign(null);
    setSelectedRowId(null);
  }

  const renderCampaignDetails = () => (
    <div className="campaign-details-card">
      <div className="details-header-area" >
        <p className="details-title-top">CAMPAIGN DETAILS</p>
      </div>
      <div className="details-body-content">
        <div className="details-content-box">
          {[
            { label: "Campaign Name", value: selectedCampaign.campaign_name },
            { label: "Total Contacts", value: selectedCampaign.total },
            { label: "Destination URL", value: selectedCampaign.destination_url },
            { label: "Created Date", value: formatUtcToLocal(selectedCampaign.created) },
          ].map((field, idx) => (
            <div className="details-row-item" key={idx}>
              <p className="details-label-text">{field.label}:</p>
              <p className="details-value-text">{field.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const handleOpenUploadModal = () => {
    setOpenUploadContactsModal(true);
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status" />
        <p>Please wait...</p>
      </div>
    )
  }
  return userRole === 'customer' ? (
    <>
      <div className={`upload-contacts-container ${selectedCampaign ? 'has-selection' : ''}`}>
        <PageHelmet
          title="Upload Contacts"
          description="Upload and process your contacts with TraQR."
          url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/upload_contacts`}
        />
        <h1 className="main-heading"> Contacts</h1>
        {campaignsData.length === 0 ? (
          <div className="no-campaigns">
            <Alert variant="danger">
              No existing campaigns were found for this user.
            </Alert>
          </div>
        ) : (
          <>
            <div className="contacts-container">

              <div className="csv-info-banner">
                <div className="csv-banner-flex">
                  <div className="banner-left-content">
                    <div className="banner-title-row">
                      <FaInfoCircle className="banner-icon" />
                      <span className="banner-title">Required CSV Columns:</span>
                    </div>
                    <p className="csv-columns-text">
                      ID, envelope_name, greeting_name, ma-addr_line1, ma-city, ma-state, ma-zip
                    </p>
                  </div>
                  <a href="/sample.csv" download className='csv-download-link'>
                    <FaDownload className="download-icon" />
                    <span>Sample CSV</span>
                  </a>
                </div>
              </div>
              <div
                className={`create-grid-form ${selectedCampaign ? 'grid-to-left' : 'grid-centered'
                  } `}
              >
                <div className="create-grid-container">
                  <CustomDataGrid
                    rows={campaignRows}
                    columns={campaignColumns}
                    totalRows={campaignsData.length}
                    onRowClick={handleRowClick}
                    selectedRowId={selectedRowId}
                    toolbarProps={{
                      createAction: {
                        show: true,
                        onClick: handleOpenUploadModal,
                        label: " +Upload"
                      }
                    }}
                  />
                </div>
                {!isMobile && selectedCampaign && (
                  <div className="create-form-container">
                    {renderCampaignDetails()}
                  </div>
                )}

                {isMobile && selectedCampaign && (
                  <div className="campaign-mobile-overlay mobile-open">
                    <div className="mobile-header">
                      <IconButton size="small" style={{ color: '#fff' }} onClick={handleCloseCampaignDetails}>
                        <MdArrowBackIos />
                      </IconButton>
                      <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>Campaign Details</span>
                    </div>
                    <div className="mobile-body" >
                      {renderCampaignDetails()}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Modal */}
              <Modal
                show={openUploadContactsModal}
                onHide={() => {
                  resetUploadState()
                  setUploadCampaign(null)
                  setOpenUploadContactsModal(false)
                }}

                centered
                className="custom-modal"
              >
                <Modal.Header closeButton>
                  <Modal.Title>Upload Contacts</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  {/* Campaign dropdown */}
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-bold">Select Campaign</Form.Label>
                    <Form.Select
                      value={uploadCampaign?.campaign_name || ''}
                      onChange={(e) => {
                        const campaign = campaignsData.find(
                          c => c.campaign_name === e.target.value
                        )
                        setUploadCampaign(campaign)
                      }}
                      className="custom-select"
                    >
                      <option value="">-- Select Campaign --</option>
                      {campaignsData.map((c) => (
                        <option key={c.campaign_name} value={c.campaign_name}>
                          {c.campaign_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>


                  {/* Drag & drop upload */}
                  <div
                    className="upload-area"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault()
                      const droppedFile = e.dataTransfer.files[0]
                      handleFileChange({ target: { files: [droppedFile] } })
                    }}
                  >
                    <label htmlFor="fileInput" className="upload-label">
                      <FaCloudUploadAlt size={28} className="upload-icon" />
                      <div className="upload-text">
                        <span className="browse-text">Click to browse</span>
                        <span className="drag-text">or drag & drop CSV here</span>
                      </div>
                    </label>

                    <input
                      type="file"
                      id="fileInput"
                      accept=".csv"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      className="file-input"
                    />
                  </div>


                  {file && (
                    <div className="file-item-modern">
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
                      </div>
                      <FaTrashAlt
                        className="icon-delete-modern"
                        onClick={handleRemoveFile}
                      />
                    </div>
                  )}

                  {uploadError && <p className="error-message-modal">{uploadError}</p>}

                  <div className='message-container'>
                    {error && (
                      <>
                        <Alert data-testid="error-alert" variant="danger">
                          {error}
                        </Alert>
                        <div className="d-flex justify-content-center">
                          <Button variant="outline-primary" onClick={handleClearAndRetry}>
                            Retry upload
                          </Button>
                        </div>
                      </>
                    )}

                    {info && (
                      <Alert
                        data-testid="success-alert"
                        variant="success"
                        className="info-alert"
                      >
                        {info}
                      </Alert>
                    )}
                  </div>

                  {/* Download link for test mode */}
                  {zipFile && testMode && (
                    <div className="download-action-container">
                      <Button
                        variant="outline-success"
                        className="upload-button download-button"
                        onClick={handleDownloadAndReload}
                      >
                        <FaDownload size={20} style={{ marginRight: '8px' }} />
                        Download CSV and QR Codes
                      </Button>
                    </div>
                  )}
                </Modal.Body>

                <Modal.Footer style={{ justifyContent: 'center', gap: '10px' }}>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setOpenUploadContactsModal(false)}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="primary"
                    // className="btn-upload"
                    disabled={isUploaded}
                    onClick={handleUpload}
                  >
                    {isUploaded ? 'Uploading...' : 'Upload'}
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
          </>
        )}
      </div>
    </>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  )
}

Contacts.propTypes = {
  userRole: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
}


export default Contacts
